import { Button, SpinningLoader } from "risa-oasis-ui_v2";
import { useExtensionConnection } from "../hooks/useExtensionConnection";
import { useHealthFacilitySelection } from "../hooks/useHealthFacilitySelection";
import { useProfileDetails } from "../hooks/useProfileDetails";
import { useAuthSelector } from "../state/AuthStoreProvider";
import type { AuthRootState } from "../state/store";
import { BrowserExtensionSection } from "./profile/components/BrowserExtensionSection";
import { HealthFacilitySelect } from "./profile/components/HealthFacilitySelect";
import { ProfileInfoFields } from "./profile/components/ProfileInfoFields";
import { ProfilePictureUpload } from "./profile/components/ProfilePictureUpload";

/**
 * Profile settings page — orchestrates profile form, facility selection, and extension pairing.
 *
 * Composes three domain hooks and four presentational sub-components; owns only
 * the page layout, footer actions, and combined loading state.
 */
const ProfilePage = () => {
  const {
    providerDetails,
    profileDetails,
    loading: profileLoading,
    saveButtonDisable,
    handleValueChange,
    downloadedFilesURL,
    updateProfileDetails,
  } = useProfileDetails();

  const {
    healthFacilityOptions,
    currentHealthFacility,
    currentInternalOrganization,
    fetchingHealthFacility,
    loading: facilityLoading,
    updateFacilityId,
  } = useHealthFacilitySelection();

  const { data: singleHealthFacilityDetails } = useAuthSelector(
    (state: AuthRootState) => state.singleHealthFacilityDetails,
  );

  // Opt-out flag: the extension section is shown unless the facility explicitly
  // disables it via feature_config.enable_browser_extension = false.
  const extensionEnabled =
    singleHealthFacilityDetails?.feature_config?.enable_browser_extension !==
    false;

  const {
    deviceId,
    pairingCode,
    connectingExtension,
    extensionConnected,
    deviceIdInputKey,
    setDeviceId,
    handleDisconnectExtension,
    handleConnectExtension,
    handleCopyPairingCode,
  } = useExtensionConnection(extensionEnabled);

  const loading = profileLoading || facilityLoading;

  return (
    <div className="profile--container">
      <div className="profile--container__header">Profile</div>
      <div className="profile--container__body">
        <ProfileInfoFields
          profileDetails={profileDetails}
          onValueChange={handleValueChange}
        />
        <HealthFacilitySelect
          fetchingHealthFacility={fetchingHealthFacility}
          healthFacilityOptions={healthFacilityOptions}
          currentHealthFacility={currentHealthFacility}
          currentInternalOrganization={currentInternalOrganization}
          loading={loading}
          onFacilityChange={updateFacilityId}
        />
        <ProfilePictureUpload
          profilePicture={profileDetails.profilePicture}
          firstName={profileDetails.firstName}
          lastName={profileDetails.lastName}
          providerId={providerDetails?.id}
          loading={loading}
          onUploaded={downloadedFilesURL}
        />
        {extensionEnabled && (
          <BrowserExtensionSection
            deviceIdInputKey={deviceIdInputKey}
            deviceId={deviceId}
            extensionConnected={extensionConnected}
            connectingExtension={connectingExtension}
            pairingCode={pairingCode}
            onDeviceIdChange={setDeviceId}
            onDisconnect={handleDisconnectExtension}
            onConnect={handleConnectExtension}
            onCopyPairingCode={handleCopyPairingCode}
          />
        )}
      </div>
      <div className="profile--container__footer">
        <Button
          disabled={false}
          onClick={() => {}}
          buttonType="secondary"
          size="medium"
        >
          {" "}
          Cancel
        </Button>
        <Button
          disabled={saveButtonDisable}
          buttonType="primary"
          size="medium"
          onClick={updateProfileDetails}
        >
          Save
          {loading && <SpinningLoader />}
        </Button>
      </div>
    </div>
  );
};

export default ProfilePage;
